import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { DraftCardPreview } from '@/components/DraftCardPreview';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type DraftSummary,
  createDraft,
  deleteDraft,
  formatRelativeTime,
  listDrafts,
} from '@/lib/drafts';
import { useSession } from '@/hooks/useSession';
import { startCheckout } from '@/lib/billing';
import { consumePostAuthIntent } from '@/lib/post-auth-intent';
import { cn } from '@/lib/utils';

export function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: sessionLoading } = useSession();
  const [drafts, setDrafts] = useState<DraftSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DraftSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await listDrafts();
      setDrafts(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load drafts');
    }
  }, []);

  // Re-fetch when returning to Drafts so card previews reflect the latest saved version.
  useEffect(() => {
    void refresh();
  }, [location.key, refresh]);

  // If the user arrived here right after sign-up with a pending "upgrade to
  // {tier}" intent (clicked on the landing-page pricing cards), kick off
  // Stripe Checkout automatically.
  //
  // We wait for the session to finish loading so we can skip the redirect
  // when the user is already on that tier (defensive — guards against a
  // race where the user clicked Upgrade on landing before /api/me resolved,
  // saving an intent that would otherwise double-charge them).
  useEffect(() => {
    if (sessionLoading) return;
    const intent = consumePostAuthIntent();
    if (!intent) return;
    if (intent.kind === 'checkout' && user?.tier !== intent.tier) {
      // Fire-and-forget: startCheckout redirects the whole tab away on
      // success. If it throws we just log; the user can re-attempt from
      // Settings → Usage.
      void startCheckout(intent.tier).catch((err) => {
        console.warn('[post-auth checkout] failed to start', err);
      });
    }
  }, [sessionLoading, user?.tier]);

  async function handleNewBlank() {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const { draftId } = await createDraft({ title: 'Untitled draft' });
      navigate(`/app/d/${draftId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create draft');
    } finally {
      setCreating(false);
    }
  }

  function handleGenerateAI() {
    navigate('/app/new');
  }

  async function handleConfirmDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteDraft(deleteTarget.id);
      setDrafts((prev) => prev?.filter((d) => d.id !== deleteTarget.id) ?? null);
      setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="bg-background text-foreground min-h-screen">
      <AppHeader currentTab="drafts" />

      <main className="mx-auto max-w-[1200px] px-6 py-12 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Drafts</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {drafts === null
                ? 'Loading…'
                : drafts.length === 0
                  ? 'You have no drafts yet.'
                  : `${drafts.length} draft${drafts.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleNewBlank} disabled={creating}>
              {creating ? 'Creating…' : '✎ Editor + AI Assist'}
            </Button>
            <Button onClick={handleGenerateAI}>✦ AI Draft</Button>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive" className="mt-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {drafts === null ? (
          <DraftsLoading />
        ) : drafts.length === 0 ? (
          <EmptyState
            onGenerate={handleGenerateAI}
            onBlank={handleNewBlank}
            creating={creating}
          />
        ) : (
          <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {drafts.map((d) => (
              <DraftCard
                key={d.id}
                draft={d}
                onOpen={() => navigate(`/app/d/${d.id}`)}
                onDeleteRequest={() => setDeleteTarget(d)}
              />
            ))}
          </ul>
        )}
      </main>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!deleting}>
          <DialogHeader>
            <DialogTitle>Delete draft?</DialogTitle>
            <DialogDescription>
              {deleteTarget ? (
                <>
                  <span className="text-foreground font-medium">
                    {deleteTarget.title ?? 'Untitled draft'}
                  </span>{' '}
                  will be permanently removed. This cannot be undone.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={deleting}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => void handleConfirmDelete()}
            >
              {deleting ? 'Deleting…' : 'Delete draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DraftCard({
  draft,
  onOpen,
  onDeleteRequest,
}: {
  draft: DraftSummary;
  onOpen: () => void;
  onDeleteRequest: () => void;
}) {
  return (
    <li className="group relative">
      <Card className="hover:border-foreground/20 relative gap-0 overflow-hidden py-0 transition-colors">
        <button type="button" onClick={onOpen} className="w-full text-left">
          <DraftCardPreview
            key={`${draft.id}-v${draft.currentVersion}`}
            html={draft.previewHtml}
            subject={draft.subject}
          />
          <div className="border-border/60 border-t px-4 pb-4 pt-4">
            <p className="truncate text-sm font-medium">{draft.title ?? 'Untitled draft'}</p>
            <p className="text-muted-foreground mt-1 truncate text-xs">
              {draft.subject ?? 'No subject yet'}
            </p>
            <p className="text-muted-foreground/70 mt-2 text-xs">
              v{draft.currentVersion} · {formatRelativeTime(draft.updatedAt)}
            </p>
          </div>
        </button>
        <div className="absolute top-3 right-3 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'h-8 w-8')}
              aria-label="Draft actions"
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                variant="destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  onDeleteRequest();
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Card>
    </li>
  );
}

function EmptyState({
  onGenerate,
  onBlank,
  creating,
}: {
  onGenerate: () => void;
  onBlank: () => void;
  creating: boolean;
}) {
  return (
    <Card className="border-border bg-card mt-12 border-dashed text-center">
      <CardContent className="p-12">
        <h2 className="text-xl font-semibold tracking-tight">Create your first email</h2>
        <p className="text-muted-foreground mx-auto mt-2 max-w-md text-sm">
          Describe what you want and let SendFast write it. Or start from a blank canvas and
          drag-and-drop blocks yourself.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={onGenerate}>✦ AI Draft</Button>
          <Button variant="outline" onClick={onBlank} disabled={creating}>
            {creating ? 'Creating…' : '✎ Editor + AI Assist'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DraftsLoading() {
  return (
    <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <li key={i}>
          <Card className="gap-0 overflow-hidden py-0">
            <Skeleton className="aspect-[3/2] w-full rounded-none" />
            <CardContent className="border-border/60 space-y-2 border-t px-4 pb-4 pt-4">
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-3 w-2/5" />
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}
